/**
 * BeatScheduler: the single arbiter mechanism for the operator-comms channel.
 * It owns the render surface, the priority/cooldown/hold arbitration, the decay
 * + two-phase timers, and the line-dedup picker. CommsDirector maps game events
 * onto beats and delegates every utterance here, so there is exactly one place
 * that decides what the operator channel shows at any instant.
 *
 * BL-61: the arbiter drives a `MascotSurface` interface (not a raw element), so
 * the same beats render either onto the framed DOM portrait OR the commentary
 * TV — one arbiter, swappable surface, no duplicated event subscriptions.
 */
import type {
  FenrirExpr,
  MascotSurface,
  VeraPose,
} from "./OperatorMascot.ts";
import {
  PRIORITY,
  type BeatPriority,
  type BeatSpec,
  type Speaker,
} from "./commsContent.ts";
import { announce } from "./a11y.ts";

/**
 * D-334 (owner decision) — the chyron's visual speaker prefix ("VERA // " /
 * "FENRIR // ") is retired (the NOW-LIVE tag + the figure already name the
 * on-air caller for sighted players). A screen-reader listener has neither —
 * the whole TV is `aria-hidden`, so `announce()` is their ONLY channel — so
 * the speaker name moves INTO the announced text here instead, never lost.
 * Proper nouns, chrome-by-design in every locale (the `CALLER_META.tag`
 * precedent, `CommentaryTV.ts`) — no i18n indirection needed. */
const SPEAKER_LABEL: Record<Speaker, string> = {
  vera: "VERA",
  fenrir: "FENRIR",
  echelon: "ECHELON",
};

/** Prefix an announced line with its speaker's name (see {@link SPEAKER_LABEL}). */
function announceSpoken(speaker: Speaker, line: string): void {
  announce(`${SPEAKER_LABEL[speaker]}: ${line}`);
}

export class BeatScheduler {
  private readonly surface: MascotSurface;
  private readonly cooldownMs: number;

  private holdingPriority: BeatPriority = PRIORITY.AMBIENT;
  private holdUntil = 0;
  private lastMinorAt = -Infinity;
  private activeSpeaker: Speaker = "vera";
  private stickyGameOver = false;
  // S194 (BL-277 M3): when the feed last actually SPOKE (any caller). Seeds at
  // construction time so a fresh run never reads as an eternity of silence.
  private lastSpokeAt = performance.now();

  private decayTimer: number | null = null;
  private phaseTimer: number | null = null;
  // S193 (BL-271): shuffle-bag state per pool — remaining indices this cycle,
  // plus the last-shown index so a bag refill can't back-to-back a line.
  private bags = new WeakMap<readonly string[], number[]>();
  private lastPickIndex = new WeakMap<readonly string[], number>();

  constructor(surface: MascotSurface, cooldownMs: number) {
    this.surface = surface;
    this.cooldownMs = cooldownMs;
  }

  canPlay(beat: BeatSpec): boolean {
    if (this.stickyGameOver && beat.priority < PRIORITY.MAX) return false;
    const now = performance.now();
    const holding = now < this.holdUntil;
    if (
      this.activeSpeaker === "fenrir" &&
      holding &&
      beat.speaker === "vera" &&
      beat.priority < PRIORITY.MAX &&
      // S193 (BL-269): the wolf finishes his line against same-or-lower VERA
      // chatter, but a genuine emergency (strictly higher priority, e.g. CORE
      // CRITICAL over a MED gloat) cuts the taunt — with the readable first-
      // phase holds the old blanket block would sit on an alarm for ~2.5 s.
      beat.priority <= this.holdingPriority
    ) {
      return false;
    }
    if (beat.minor && now - this.lastMinorAt < this.cooldownMs) return false;
    if (!holding) return true;
    return beat.priority > this.holdingPriority;
  }

  /** S194 (BL-277 M3): ms since the feed last actually spoke — the dead-air
   *  gauge for the ambient system. Measured from speech START (holds are ≤6 s
   *  against a ≥30 s ambient threshold, so end-vs-start is noise). */
  quietMs(): number {
    return performance.now() - this.lastSpokeAt;
  }

  playVera(
    beat: BeatSpec,
    pose: VeraPose,
    line: string,
    holdOverride?: number,
    force = false,
  ): void {
    if (!force && !this.canPlay(beat)) return;
    this.clearTimers();
    this.lastSpokeAt = performance.now();
    this.activeSpeaker = "vera";
    this.surface.setCharacter("vera");
    this.surface.setPose(pose);
    this.surface.setQuip(line);
    // D-331: the TV is now a message surface too (narration/praise/status
    // rerouted off the grid), so it needs the SAME a11y mirror GameBanner has
    // always had — one choke point, screen-reader parity regardless of which
    // surface a message lands on. D-334: the speaker's name rides IN the
    // announced text (see announceSpoken) now that the visual chip is gone.
    announceSpoken("vera", line);
    const holdFor = holdOverride ?? this.holdMs(line);
    this.startHold(beat.priority, holdFor, beat.minor);
    if (!this.stickyGameOver) {
      this.decayTimer = window.setTimeout(() => {
        this.decayTimer = null;
        this.surface.setPose("idle");
        this.surface.clearQuip();
      }, holdFor);
    }
  }

  playFenrir(
    expr: FenrirExpr,
    line: string,
    priority: BeatPriority,
    holdMs: number,
    minor: boolean,
    glitch = false,
  ): void {
    if (glitch) this.surface.pulseGlitch();
    this.lastSpokeAt = performance.now();
    this.activeSpeaker = "fenrir";
    this.surface.setCharacter("fenrir", expr);
    this.surface.setQuip(line);
    announceSpoken("fenrir", line); // D-331/D-334: a11y parity — see playVera.
    this.startHold(priority, holdMs, minor);
    this.decayTimer = window.setTimeout(() => {
      this.decayTimer = null;
      this.surface.clearGlitch();
      this.surface.setCharacter("vera");
      this.surface.clearQuip();
      this.surface.setPose("idle");
      this.activeSpeaker = "vera";
    }, holdMs);
  }

  /** S130 (Story Bible §6.3): ECHELON's SIGNAL OVERRIDE — the scripted-track
   *  takeover beat. Mirrors playFenrir's hold/decay contract (the surface
   *  always returns to VERA idle), but renders through the surface's hijack
   *  seam: she takes the screen, she never becomes a caller. Surfaces without
   *  the optional hijack get a glitch + plain quip (legacy portrait). */
  playEchelon(line: string, priority: BeatPriority, holdMs: number): void {
    if (this.stickyGameOver) return;
    this.clearTimers();
    this.lastSpokeAt = performance.now();
    this.activeSpeaker = "echelon";
    if (this.surface.playHijack) {
      this.surface.playHijack(line);
    } else {
      this.surface.pulseGlitch();
      this.surface.setQuip(line);
    }
    announceSpoken("echelon", line); // D-331/D-334: a11y parity — see playVera.
    this.startHold(priority, holdMs, false);
    this.decayTimer = window.setTimeout(() => {
      this.decayTimer = null;
      this.surface.clearHijack?.();
      this.surface.clearGlitch();
      this.surface.setCharacter("vera");
      this.surface.clearQuip();
      this.surface.setPose("idle");
      this.activeSpeaker = "vera";
    }, holdMs);
  }

  /** Sticky terminal beat: VERA "down", holds forever, blocks all but MAX. */
  playGameOver(line: string): void {
    this.clearTimers();
    this.lastSpokeAt = performance.now();
    this.stickyGameOver = true;
    this.startHold(PRIORITY.MAX, Number.POSITIVE_INFINITY, false);
    this.activeSpeaker = "vera";
    this.surface.setCharacter("vera");
    this.surface.clearGlitch();
    this.surface.setPose("down");
    this.surface.setQuip(line);
    announceSpoken("vera", line); // D-331/D-334: a11y parity — see playVera.
  }

  /**
   * Register that the operator spoke through another surface (the swap-screen
   * commander). Holds the portrait briefly so it does not talk over the same
   * voice when the overlay closes — keeping the arbiter the single channel.
   */
  noteExternalSpeech(priority: BeatPriority, holdMs: number): void {
    if (this.stickyGameOver) return;
    this.startHold(priority, holdMs, true);
  }

  /** Schedule the second phase of a two-phase beat. Leaves the decay timer
   *  set by the first phase untouched; only one phase timer can be pending. */
  schedulePhase(delayMs: number, cb: () => void): void {
    if (this.phaseTimer !== null) clearTimeout(this.phaseTimer);
    this.phaseTimer = window.setTimeout(() => {
      this.phaseTimer = null;
      cb();
    }, delayMs);
  }

  startHold(priority: BeatPriority, holdMs: number, minor: boolean): void {
    const now = performance.now();
    this.holdingPriority = priority;
    this.holdUntil = now + holdMs;
    if (minor) this.lastMinorAt = now;
  }

  holdMs(text: string): number {
    return Math.min(6000, Math.max(2800, 2800 + 45 * text.length));
  }

  /** S193 (BL-271): shuffle-bag pick — every line plays once before any line
   *  repeats (the old reroll-once dedup still hit 11 % immediate repeats on
   *  3-line pools and had no within-run memory). Render-side RNG only; the
   *  first draw after a refill avoids the previous bag's final line so bag
   *  boundaries can never show the same line back-to-back. */
  pick(pool: readonly string[]): string {
    if (pool.length <= 1) return pool[0] ?? "";
    let bag = this.bags.get(pool);
    if (!bag || bag.length === 0) {
      bag = pool.map((_, i) => i);
      this.bags.set(pool, bag);
    }
    let at = Math.floor(Math.random() * bag.length);
    const last = this.lastPickIndex.get(pool);
    if (bag[at] === last && bag.length > 1) {
      at = (at + 1 + Math.floor(Math.random() * (bag.length - 1))) % bag.length;
    }
    const index = bag[at]!;
    bag.splice(at, 1);
    this.lastPickIndex.set(pool, index);
    return pool[index] ?? "";
  }

  clearTimers(): void {
    if (this.decayTimer !== null) {
      clearTimeout(this.decayTimer);
      this.decayTimer = null;
    }
    if (this.phaseTimer !== null) {
      clearTimeout(this.phaseTimer);
      this.phaseTimer = null;
    }
  }
}
